import type { Template } from '@lumapps/content-templates/types';
import { CustomContentType } from '@lumapps/content-types/types';
import { Instance } from '@lumapps/instance/types';
import { FlagProps } from '@lumapps/lumx/react';
import { Media } from '@lumapps/medias/types';
import { TranslatedMetadataListV2 } from '@lumapps/metadata/types';
import type { WithReactions } from '@lumapps/reactions-core/api/types';
import { Route } from '@lumapps/router';
import { SubscriptionType } from '@lumapps/subscriptions/types';
import { GLOBAL, TranslateObject } from '@lumapps/translations';
import { User } from '@lumapps/user/types';
import type { ObjectValues } from '@lumapps/utils/types/ObjectValues';
import { RecursivePartial } from '@lumapps/utils/types/RecursivePartial';
import type { Row } from '@lumapps/widget-layout/types';

import { CONTENT_ACTIONS } from './constants';

export enum ContentHeaderDisplayType {
    overlay = 'overlay',
    gradient = 'gradient',
    textBackground = 'text-background',
}

export enum LegacyContentStatus {
    live = 'LIVE',
    archive = 'ARCHIVE',
    draft = 'DRAFT',
    toValidate = 'TO_VALIDATE',
    unarchive = 'UNARCHIVE',
    refused = 'REFUSED',
    pending = 'PENDING',
}

export const TranslationsAndColorForContentStatus: Record<
    LegacyContentStatus,
    { key: string; color: FlagProps['color'] }
> = {
    LIVE: {
        key: GLOBAL.PUBLISHED,
        color: 'green',
    },
    ARCHIVE: {
        key: GLOBAL.ARCHIVED,
        color: 'grey',
    },
    DRAFT: {
        key: GLOBAL.DRAFT,
        color: 'yellow',
    },
    TO_VALIDATE: {
        key: GLOBAL.DRAFT,
        color: 'yellow',
    },
    UNARCHIVE: {
        key: GLOBAL.DRAFT,
        color: 'yellow',
    },
    REFUSED: {
        key: GLOBAL.REFUSED,
        color: 'yellow',
    },
    PENDING: {
        key: GLOBAL.SCHEDULED,
        color: 'green',
    },
};

export enum ContentType {
    community = 'community',
    content = 'content',
}

/** Type used to reference a link to a content. */
export interface ContentLinkRef {
    /** Content id. */
    id?: string;
    /** Content slug. */
    slug?: string;
    /** Content title */
    title?: string;
    /** Content type. */
    type?: string;
    /** Content external key id */
    externalKey?: string;
    /** Anchor to scroll to on the content page. */
    anchor?: Route['anchor'];
    /** Content page query params. */
    query?: Route['query'];
    /** Whether this content the home page or not. */
    isHomePage?: boolean;
    /** Whether this content is known to be compatible with layout v2 or not. */
    isV2Compatible?: boolean;
    /** Content external link */
    link?: string;
    /**
     * Content instance.
     * Used to generate links cross instance.
     */
    instance?: Partial<Pick<Instance, 'id' | 'slug'>>;
    /**
     * Whether to use the parent path included in the slug or use only the last part of the slug.
     * Useful for navigation items.
     */
    keepParentPath?: boolean;
    /**
     * Whether the content link ref has stale data or not (slug could have changed, instance slug could have changed, etc.)
     */
    hasStaleData?: boolean;
}

export type ContentTemplate = Template<Row>;

export interface ContentHeader {
    uid?: string;
    name?: string;
    author?: string;
    instance?: string;
    updatedBy?: string;
    properties?: {
        random?: boolean;
        autoplay?: boolean;
        layoutPosition?: number;
        media?: Media[];
        wrapperHeight?: number;
        interval?: number;
        style?: ContentHeaderDisplayType;
    };
    createdAt?: string;
    media?: string[];
    customer?: string;
    public?: boolean;
    heritable?: boolean;
    updatedAt?: string;
    height?: number;
    id?: string;
}

/** @see https://lumapps-api.stoplight.io/docs/lumapps-public-api/d78acf1cf0543-content */
export interface BaseContent extends Partial<WithReactions> {
    /** The content id */
    id?: string;
    uid?: string;
    /** The content title */
    title?: TranslateObject<string>;
    /** The content slug */
    slug?: TranslateObject<string>;
    /** AKA `visible by` The IDs of the feeds allowed to view the content. */
    feedKeys?: string[];
    /** The IDs of the spaces (communities) the content is visible in. Only used when the `content-in-space` FF is enabled. */
    communityIds?: string[];
    isFeatured?: boolean;
    featuredStartDate?: string;
    featuredStartDateInput?: {
        date?: string;
        hour?: number;
        min?: number;
    };
    featuredEndDate?: string;
    featuredEndDateInput?: {
        date?: string;
        hour?: number;
        min?: number;
    };
    editors?: string[];
    deleters?: string[];
    publishers?: string[];
    archivers?: string[];
    type: string;
    instance?: string;
    analyticsViewSum?: number;
    startDate?: string;
    startDateInput?: {
        date?: string;
        hour?: number;
        min?: number;
    };
    endDate?: string;
    endDateInput?: {
        date?: string;
        hour?: number;
        min?: number;
    };
    instanceDetails?: Pick<Instance, 'slug' | 'name' | 'id'>;
    adminKeys?: string[];
    properties?: Record<string, any>;
    /** The content external link */
    link?: TranslateObject<string>;
    status?: LegacyContentStatus;
    canonicalUrl?: Record<string, any>;
    header?: string;
    isHomepage?: boolean;
    view?: string;
    excerpt?: TranslateObject;
    headerDetails?: ContentHeader;
    customContentType?: string;
    author?: string;
    authorId?: string;
    updatedAt?: string;
    createdAt?: string;
    lastPublishedAt?: string;
    canContribute?: boolean;
    relevantComment?: string;
    canEdit?: boolean;
    /** AKA `highlighted for` The IDs of the feeds for which this content is promoted. */
    featuredFeedKeys?: string[];
    /** The url to the content thumbnail. Only used as a fallback if mediaThumbnail is missing */
    thumbnail?: string;
    /** The alt text of the featured image of the content */
    thumbnailAltText?: TranslateObject<string>;
    mediaThumbnail?: RecursivePartial<Media>;
    notificationsEnabled?: boolean;
    /** The list of feeds IDs to notify. */
    notifyFeedKeys?: string[];
    /** Whether the content is loading or fields are being fetched. */
    isLoading?: boolean;
    /** Number of comments. */
    comments?: number;
    /** Whether the content has been liked by the connected user. */
    liked?: boolean;
    /** Number of likes. */
    likes?: number;
    /** List of user having liked the content. */
    likesList?: {
        items: User[];
        isLoaded: boolean;
    };
    /** Whether the content is followed by the connected user. */
    following?: boolean;
    /** Whether the content is followed by the connected user. */
    subscription?: SubscriptionType;
    /** Information concerning the author of the content. */
    authorDetails?: User;
    /** When a content is written on behalf a user, the writer details is filled with users' informations. */
    writerDetails?: User;
    /** Information concerning the last user that updated the content. */
    updatedByDetails?: User;
    /** Whether the content is public */
    public?: boolean;
    /** Publication date of the content. */
    publicationDate?: string;
    /** The selected tags ids of a content (do not use outside of a content). */
    customContentTypeTags?: string[];
    /** Information concerning the custom content type of the content. */
    customContentTypeDetails?: Pick<
        CustomContentType,
        'tags' | 'icon' | 'id' | 'endDateDelta' | 'isEndDateMandatory' | 'isWorkflowEnabled' | 'name'
    >;
    lastRevision?: {
        createdAt: string;
        createdByDetails?: User;
        updatedAt: string;
        revisionKey: string;
        status: LegacyContentStatus;
        title: TranslateObject;
        version: string;
    };
    /** Information concerning the metadata of the content. */
    metadataDetails?: TranslatedMetadataListV2;
    /** Whether the piece of content has a comment widget */
    hasCommentWidget?: boolean;
    /** The list of post categories (used on a community context only).
     * Note: see `customContentTypeTags` for content related tags
     */
    tags?: any[];
    /** The details of post categories (used on a community context only).
     * Note: see `customContentTypeTags` for content related tags
     */
    tagsDetails?: any[];
    /** optional external key that returns directory id */
    externalKey?: string;
}

export interface ReadModeContent extends BaseContent {
    id: string;
    /** Note: isDesignerMode is always false in content view */
    isDesignerMode?: false;
    /** List of content's metadata ids */
    metadata?: string[];
    template?: ContentTemplate;
}

export interface WriteModeContent extends BaseContent {
    id?: string;
    /** Note: isDesignerMode is always true in content edition */
    isDesignerMode: true;
    customer: string;
    metadata?: Record<string, string[] | string>;
    template?: ContentTemplate;
    liveRevision?: Pick<
        BaseContent,
        'endDate' | 'startDate' | 'status' | 'lastPublishedAt' | 'lastRevision' | 'isHomepage'
    >;
}

export type Content = ReadModeContent | WriteModeContent;

export type ContentAction = ObjectValues<typeof CONTENT_ACTIONS>;
